<script lang="ts">
	import Router from "svelte-spa-router";
	import { wrap } from "svelte-spa-router/wrap";
	import { onMount } from "svelte";
	
	// Import new components
	import LoadingSpinner from "./components/LoadingSpinner.svelte";
	import ErrorBoundary from "./components/ErrorBoundary.svelte";
	
    // Import stores
    import { visualizationStore, uiIsLoading, error, visualizationActions } from "./stores/visualization";
	import { DataService } from "./lib/dataService";
	
	// Import route components
	import KeywordSphere from "./routes/KeywordSphere.svelte";
	import VideoUniverse from "./routes/VideoUniverse.svelte";
	import Notfound from "./routes/Notfound.svelte";

    const routes = {
        "/": VideoUniverse,
        "/universe": VideoUniverse, // Alias
        "/sphere": KeywordSphere,
        "*": Notfound,
    };

	let dataService: DataService;

	onMount(async () => {
		dataService = DataService.getInstance();
		await loadData();
	});

	async function loadData() {
		try {
			const videos = await dataService.loadVideoData();
			dataService.generateKeywords(videos);
		} catch (err) {
			console.error('Failed to load data:', err);
		}
	}

	function handleRetry() {
		visualizationActions.reset();
		loadData();
	}

	function handleSearchInput(e: Event) {
		const target = e.target as HTMLInputElement;
		visualizationActions.setSearchQuery(target?.value ?? '');
	}
</script>

<style>
	:global(body) {
		margin: 0;
		padding: 0;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
		overflow: hidden;
	}

	.navigation {
		position: fixed;
		top: 20px;
		left: 20px;
		z-index: 1000;
		display: flex;
		gap: 20px;
	}

	.nav-link {
		background: rgba(0, 0, 0, 0.8);
		color: white;
		text-decoration: none;
		padding: 1rem 1.5rem;
		border-radius: 8px;
		transition: all 0.3s ease;
		backdrop-filter: blur(10px);
		border: 1px solid rgba(255, 255, 255, 0.1);
		min-width: 160px;
	}

	.nav-link:hover {
		background: rgba(255, 62, 0, 0.9);
		transform: translateY(-2px);
		box-shadow: 0 8px 25px rgba(255, 62, 0, 0.3);
	}

	.nav-link h1 {
		margin: 0 0 0.5rem 0;
		font-size: 1.2rem;
		font-weight: 600;
		color: inherit;
	}

	.nav-link p {
		margin: 0;
		font-size: 0.9rem;
		opacity: 0.8;
		color: inherit;
	}

	.search input {
		height: 48px;
		padding: 0 12px;
		border-radius: 8px;
		border: 1px solid rgba(255, 255, 255, 0.15);
		background: rgba(0, 0, 0, 0.6);
		color: #fff;
		outline: none;
		width: 240px;
	}

	@media (max-width: 768px) {
		.navigation {
			flex-direction: column;
			gap: 10px;
		}
		
		.nav-link {
			min-width: 140px;
			padding: 0.8rem 1rem;
		}
		
		.nav-link h1 {
			font-size: 1rem;
		}
		
		.nav-link p {
			font-size: 0.8rem;
		}
	}

.route-wrapper :global(*) {
  animation: routeFade 250ms ease;
}

@keyframes routeFade {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>

{#if !$uiIsLoading}
  <!-- Navigation -->
  <nav class="navigation">
      <a href="#/" class="nav-link">
          <h1>Video Universe</h1>
          <p>Browse in 3D space</p>
      </a>
      <a href="#/sphere" class="nav-link">
          <h1>Keyword Sphere</h1>
          <p>Explore by topics</p>
      </a>
      <div class="search">
        <input
          type="search"
          placeholder="Search videos or keywords..."
          on:input={handleSearchInput}
        />
      </div>
  </nav>
{/if}

<!-- Loading and Error States -->
{#if $uiIsLoading}
    <LoadingSpinner message="Loading your video universe..." />
{/if}

{#if $error}
	<ErrorBoundary error={$error} onRetry={handleRetry} />
{/if}

<!-- Main Router -->
{#if !$uiIsLoading}
  <div class="route-wrapper">
    <Router {routes} />
  </div>
{/if}
